import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiGetCompanyByCode } from "services/CompanyService";
import { setWhitelabel } from "store/base/commonSlice";
import { setThemeColor } from "store/theme/themeSlice";
import getDomain from "utils/getDomain";
import parseWhitelabelInfo from "utils/parseWhitelabelInfo";
import { applyWhitelabelTheme } from "utils/applyWhitelabelTheme";

const inactivePayload = {
  loaded: true,
  active: false,
  company: null,
  config: null,
};

// Axios timeout'u 60sn; yavaş/kesintili mobil bağlantılarda kullanıcı o süre
// boyunca boş splash ekranında kalıyordu. Bu süreden sonra uygulamayı aç.
const BOOTSTRAP_UNBLOCK_MS = 6000;

const WhitelabelSplash = () => (
  <div className="flex h-screen w-full items-center justify-center bg-[#EFEFEF]">
    {/* Neutral spinner — avoid Mole/brand colors before config is known */}
    <div
      className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500"
      role="status"
      aria-label="Loading"
    />
  </div>
);

/**
 * Loads company by subdomain (GetByCode), applies whitelabel theme,
 * and blocks the app until ready so default Mole colors don't flash.
 */
const WhitelabelBootstrap = ({ children }) => {
  const dispatch = useDispatch();
  const loaded = useSelector((state) => state.base.common.whitelabel.loaded);

  useEffect(() => {
    let cancelled = false;

    const unblockTimer = setTimeout(() => {
      if (!cancelled) dispatch(setWhitelabel({ loaded: true }));
    }, BOOTSTRAP_UNBLOCK_MS);

    const bootstrap = async () => {
      const code = getDomain();
      if (!code) {
        if (!cancelled) dispatch(setWhitelabel(inactivePayload));
        return;
      }

      try {
        const response = await apiGetCompanyByCode(code);
        if (cancelled) return;

        const company = response?.data?.data ?? null;
        // Public GetByCode DTO only: { id, logo, haswl, wl }
        const hasWhitelabel = !!company?.haswl;
        const config = parseWhitelabelInfo(company?.wl);
        const active = hasWhitelabel && !!config;

        if (active && config?.primaryColor) {
          applyWhitelabelTheme(config.primaryColor);
          dispatch(setThemeColor("kostebek"));
        }

        dispatch(
          setWhitelabel({
            loaded: true,
            active,
            company,
            config: active ? config : null,
          })
        );
      } catch {
        if (!cancelled) dispatch(setWhitelabel(inactivePayload));
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      clearTimeout(unblockTimer);
    };
  }, [dispatch]);

  if (!loaded) {
    return <WhitelabelSplash />;
  }

  return children;
};

export default WhitelabelBootstrap;
